import { useState } from "react";
import { User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PIVOTS = [
    { key: "career_shift", label: "Leaving a career, becoming something new" },
    { key: "relationship_shift", label: "A relationship is changing shape" },
    { key: "identity_evolution", label: "Rediscovering who I am now" },
    { key: "grief_loss", label: "Moving through grief or loss" },
    { key: "motherhood_transition", label: "A motherhood transition" },
    { key: "other", label: "Something else that doesn't have a name yet" },
];

export default function Profile() {
    const { user, refreshUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [pivot, setPivot] = useState(user?.life_pivot || "");
    const [detail, setDetail] = useState(user?.life_pivot_detail || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/user/profile", { name, life_pivot: pivot, life_pivot_detail: detail || null });
            await refreshUser();
            toast.success("Held.");
        } catch (e) {
            toast.error("Could not save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto w-full px-6 md:px-10 py-10 md:py-14" data-testid="profile-page">
            <div className="mb-10 pl-12 md:pl-0">
                <div className="flex items-center gap-2 text-brand-charcoal/60 text-xs uppercase tracking-widest">
                    <UserIcon className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                    You
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-darkPurple leading-tight tracking-tight">
                    Your season, right now.
                </h1>
                <p className="mt-3 text-brand-charcoal/75 max-w-2xl">This can change. So can you.</p>
            </div>

            <div className="rounded-2xl bg-white border border-brand-darkPurple/10 shadow-soft-card p-7 md:p-8 space-y-6">
                <div>
                    <Label className="text-brand-darkPurple text-sm">Name</Label>
                    <Input
                        data-testid="profile-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1.5 bg-brand-cream/50 border-brand-darkPurple/10 h-11 focus-visible:ring-brand-gold/50"
                    />
                </div>
                <div>
                    <Label className="text-brand-darkPurple text-sm">Email</Label>
                    <Input value={user?.email || ""} disabled className="mt-1.5 bg-brand-cream/50 border-brand-darkPurple/10 h-11 text-brand-charcoal/70" />
                </div>

                <div>
                    <Label className="text-brand-darkPurple text-sm mb-3 block">Current life pivot focus</Label>
                    <RadioGroup value={pivot} onValueChange={setPivot} className="space-y-2">
                        {PIVOTS.map((p) => (
                            <label
                                key={p.key}
                                htmlFor={`pivot-${p.key}`}
                                data-testid={`profile-pivot-${p.key}`}
                                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                                    pivot === p.key ? "border-brand-darkPurple bg-brand-cream/60" : "border-brand-darkPurple/12 hover:bg-brand-cream/40"
                                }`}
                            >
                                <RadioGroupItem value={p.key} id={`pivot-${p.key}`} className="mt-0.5" />
                                <span className="text-sm text-brand-darkPurple leading-relaxed">{p.label}</span>
                            </label>
                        ))}
                    </RadioGroup>
                </div>

                <div>
                    <Label className="text-brand-darkPurple text-sm">A little more, if you want</Label>
                    <Textarea
                        data-testid="profile-detail-input"
                        value={detail}
                        onChange={(e) => setDetail(e.target.value)}
                        placeholder="Anything that helps the companion meet you where you are."
                        className="mt-1.5 min-h-[100px] bg-brand-cream/50 border-brand-darkPurple/10 resize-none focus-visible:ring-brand-gold/50"
                    />
                </div>

                <div className="flex justify-end pt-2">
                    <Button
                        data-testid="profile-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-7"
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
